/**
 * "Workbook" button + dialog for a takeoff or board estimate: link the Eos
 * Estimating Workbook project copy (or create one from the RELEASE template in
 * the job's 02-Working/01-Estimating), preview a push, push, read back.
 * Live result via `workbook.push_*` socket frames, with polling as a fallback.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ACTION_LABEL,
  CONFLICT_ACTIONS,
  isTemplateTitle,
  syncStatusText,
  parseFolderId,
  planSummary,
  type WorkbookApi,
  type WorkbookConflictAction,
  type WorkbookLinkDTO,
  type WorkbookPlan,
  type WorkbookReadback,
  type WorkbookSuggestion,
  type WorkbookTotals,
} from '../workbook';
import { isOptionScope } from '../scopes';
import { Modal, relativeTime, toast } from './basics';

export type WorkbookFrameListener = (type: string, data: Record<string, unknown>) => void;
export type WorkbookSubscribe = (fn: WorkbookFrameListener) => () => void;

const STATUS_LABEL: Record<string, string> = { ok: 'Pushed', partial: 'Pushed with skips', error: 'Push failed', running: 'Pushing…' };

/** The takeoff's workbook link, kept fresh from `workbook.*` / `takeoff.changed` frames. */
export function useWorkbookLink(client: WorkbookApi | null | undefined, subscribe?: WorkbookSubscribe): WorkbookLinkDTO | null {
  const [link, setLink] = useState<WorkbookLinkDTO | null>(null);
  const refresh = useCallback(() => {
    if (!client) return;
    client
      .get()
      .then(setLink)
      .catch(() => undefined);
  }, [client]);
  useEffect(refresh, [refresh]);
  useEffect(
    () =>
      subscribe?.((type) => {
        if (type === 'workbook.sync' || type === 'workbook.push_result' || type === 'takeoff.changed') refresh();
      }),
    [subscribe, refresh],
  );
  return link;
}

export function WorkbookButton({ client, title, subscribe, canEdit = true }: { client: WorkbookApi; title: string; subscribe?: WorkbookSubscribe; canEdit?: boolean }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState<WorkbookLinkDTO | null>(null);
  const refresh = useCallback(() => {
    client
      .get()
      .then(setLink)
      .catch(() => undefined);
  }, [client]);
  useEffect(refresh, [refresh]);
  useEffect(
    () =>
      subscribe?.((type) => {
        if (type.startsWith('workbook.') || type === 'takeoff.changed') refresh();
      }),
    [subscribe, refresh],
  );
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 10_000);
    return () => clearInterval(t);
  }, []);
  const conflicts = link?.conflicts?.length ?? 0;
  const status = link?.linked ? syncStatusText(link) : '';
  return (
    <>
      <button
        className="rl-btn"
        data-size="sm"
        data-testid="workbook-open"
        data-sync={link?.last_sync_status || undefined}
        title={link?.linked ? `${link.title} — ${status}` : 'Link the Eos Estimating Workbook'}
        onClick={() => setOpen(true)}
      >
        Workbook
        {link?.linked ? (
          <span className="rl-muted" data-testid="workbook-button-status">
            {' · '}
            {link.last_sync_status === 'syncing' ? 'Syncing…' : link.last_sync_status === 'error' ? 'Sync failed' : link.last_sync_at ? '✓' : '…'}
            {conflicts ? ` · ⚠ ${conflicts}` : ''}
          </span>
        ) : null}
      </button>
      {open ? <WorkbookDialog client={client} title={title} subscribe={subscribe} canEdit={canEdit} onClose={() => setOpen(false)} onChange={setLink} /> : null}
    </>
  );
}

export function WorkbookDialog(p: { client: WorkbookApi; title: string; subscribe?: WorkbookSubscribe; canEdit: boolean; onClose(): void; onChange?(l: WorkbookLinkDTO): void }) {
  const { client, onChange } = p;
  const [link, setLink] = useState<WorkbookLinkDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [plan, setPlan] = useState<WorkbookPlan | null>(null);
  const [clean, setClean] = useState(true);
  const [readback, setReadback] = useState<WorkbookReadback | null>(null);
  const poll = useRef<ReturnType<typeof setInterval> | null>(null);

  const update = useCallback(
    (l: WorkbookLinkDTO) => {
      setLink(l);
      onChange?.(l);
      if (l.last_readback && 'rows' in l.last_readback) setReadback(l.last_readback as WorkbookReadback);
    },
    [onChange],
  );
  const load = useCallback(() => client.get().then(update).catch((e: Error) => setError(e.message)), [client, update]);
  useEffect(() => {
    void load();
    return () => {
      if (poll.current) clearInterval(poll.current);
    };
  }, [load]);
  useEffect(
    () =>
      p.subscribe?.((type, data) => {
        if (type === 'workbook.push_result') {
          if (poll.current) clearInterval(poll.current);
          poll.current = null;
          setBusy(null);
          const ok = Boolean(data.success);
          toast(ok ? 'Workbook updated' : `Push failed: ${String(data.error ?? '')}`, ok ? 'info' : 'error');
          setPlan(null);
          void load();
        } else if (type.startsWith('workbook.') || type === 'takeoff.changed') void load();
      }),
    [p.subscribe, load],
  );
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 5_000);
    return () => clearInterval(t);
  }, []);

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as { body?: { error?: string } }).body?.error ?? (e as Error).message);
    } finally {
      setBusy((b) => (b === label ? null : b));
    }
  };

  const startPush = () =>
    run('push', async () => {
      await client.push(clean);
      setBusy('pushing');
      if (poll.current) clearInterval(poll.current);
      const started = Date.now();
      poll.current = setInterval(() => {
        client
          .get()
          .then((l) => {
            update(l);
            if (l.last_push_status !== 'running' || Date.now() - started > 180_000) {
              if (poll.current) clearInterval(poll.current);
              poll.current = null;
              setBusy(null);
              setPlan(null);
            }
          })
          .catch(() => undefined);
      }, 3000);
    });

  return (
    <Modal title="Eos Estimating Workbook" onClose={p.onClose}>
      <div className="rl-workbook" data-testid="workbook-dialog" style={{ minWidth: 460, maxWidth: 640 }}>
        {error ? (
          <div className="rl-banner" data-kind="error" role="alert" data-testid="workbook-error">
            {error}
          </div>
        ) : null}
        {!link ? <p className="rl-muted">Loading…</p> : link.linked ? (
          <Linked
            link={link}
            canEdit={p.canEdit}
            busy={busy}
            plan={plan}
            clean={clean}
            setClean={setClean}
            readback={readback}
            onPreview={() => run('plan', async () => setPlan(await client.plan(clean)))}
            onPush={startPush}
            onReadback={() => run('readback', async () => setReadback(await client.readback()))}
            onAutoSync={(on) => run('auto', async () => update(await client.setAutoSync(on)))}
            onSyncNow={() => run('sync', async () => void (await client.syncNow()))}
            onResolve={(id, action) => run(`resolve:${id}`, async () => update(await client.resolve(id, action)))}
            onRestore={(key) => run(`restore:${key}`, async () => update(await client.restore(key)))}
            onUnlink={() =>
              run('unlink', async () => {
                update(await client.unlink());
                setPlan(null);
                setReadback(null);
              })
            }
          />
        ) : (
          <NotLinked client={client} canEdit={p.canEdit} busy={busy} run={run} onLinked={update} />
        )}
      </div>
    </Modal>
  );
}

function NotLinked({ client, canEdit, busy, run, onLinked }: { client: WorkbookApi; canEdit: boolean; busy: string | null; run(label: string, fn: () => Promise<void>): Promise<void>; onLinked(l: WorkbookLinkDTO): void }) {
  const [mode, setMode] = useState<'create' | 'link'>('create');
  const [sug, setSug] = useState<WorkbookSuggestion | null>(null);
  const [name, setName] = useState('');
  const [folder, setFolder] = useState('');
  const [url, setUrl] = useState('');
  useEffect(() => {
    client
      .suggest()
      .then((s) => {
        setSug(s);
        setName(s.name);
      })
      .catch(() => undefined);
  }, [client]);
  if (!canEdit) return <p className="rl-muted">No workbook is linked yet. Someone who can edit this estimate can link one.</p>;
  const folderId = folder.trim() ? parseFolderId(folder) : sug?.folder?.id ?? '';
  const nameBad = isTemplateTitle(name);
  const urlBad = /template|release/i.test(url);
  return (
    <div>
      <p className="rl-muted" style={{ marginTop: 0 }}>
        Counts and areas go into a <strong>project copy</strong> of the Estimating Workbook (template v{sug?.template.version ?? '…'}). The RELEASE template itself is never written to.
      </p>
      <div role="tablist" style={{ display: 'flex', gap: 6, margin: '8px 0' }}>
        <button className="rl-btn" data-size="sm" aria-pressed={mode === 'create'} data-variant={mode === 'create' ? 'primary' : undefined} onClick={() => setMode('create')} data-testid="workbook-mode-create">
          Create project copy
        </button>
        <button className="rl-btn" data-size="sm" aria-pressed={mode === 'link'} data-variant={mode === 'link' ? 'primary' : undefined} onClick={() => setMode('link')} data-testid="workbook-mode-link">
          Link existing copy
        </button>
      </div>
      {mode === 'create' ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <label>
            <span className="rl-muted">Name</span>
            <input className="rl-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="workbook-name" />
          </label>
          {nameBad ? <span className="rl-error">A project copy cannot be called Template or RELEASE.</span> : null}
          <label>
            <span className="rl-muted">Folder {sug?.path?.length ? `— ${sug.path.join(' / ')}` : sug ? '— none found; paste a Drive folder link' : ''}</span>
            <input className="rl-input" placeholder={sug?.folder ? 'Paste a different Drive folder link (optional)' : 'Drive folder link'} value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="workbook-folder" />
          </label>
          <div>
            <button
              className="rl-btn"
              data-variant="primary"
              disabled={!!busy || !name.trim() || nameBad || (folder.trim() !== '' && !folderId)}
              data-testid="workbook-create"
              onClick={() => run('create', async () => onLinked(await client.create(name.trim(), folderId || undefined)))}
            >
              {busy === 'create' ? 'Copying the template…' : 'Create and link'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          <label>
            <span className="rl-muted">Google Sheets link to the project copy</span>
            <input className="rl-input" placeholder="https://docs.google.com/spreadsheets/d/…" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="workbook-url" />
          </label>
          <div>
            <button className="rl-btn" data-variant="primary" disabled={!!busy || !url.trim()} data-testid="workbook-link" onClick={() => run('link', async () => onLinked(await client.link(url.trim())))}>
              {busy === 'link' ? 'Checking…' : 'Link'}
            </button>
          </div>
          {urlBad ? <span className="rl-muted">That looks like the template — the server will refuse it.</span> : null}
        </div>
      )}
    </div>
  );
}

function Linked(p: {
  link: WorkbookLinkDTO;
  canEdit: boolean;
  busy: string | null;
  plan: WorkbookPlan | null;
  clean: boolean;
  setClean(v: boolean): void;
  readback: WorkbookReadback | null;
  onPreview(): void;
  onPush(): void;
  onReadback(): void;
  onUnlink(): void;
  onAutoSync(on: boolean): void;
  onSyncNow(): void;
  onResolve(id: string, action: WorkbookConflictAction): void;
  onRestore(key: string): void;
}) {
  const { link, plan, readback } = p;
  const s = plan ? planSummary(plan) : null;
  const pushing = p.busy === 'pushing' || p.busy === 'push' || link.last_push_status === 'running';
  const last = link.last_push;
  const conflicts = link.conflicts ?? [];
  const syncing = link.last_sync_status === 'syncing' || p.busy === 'sync';
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div>
        <a href={link.url} target="_blank" rel="noopener noreferrer" data-testid="workbook-title">
          {link.title}
        </a>
        <div className="rl-muted" style={{ fontSize: 12 }}>
          Template v{link.template_version || '?'} · linked {relativeTime(link.linked_at)}
          {link.linked_by ? ` by ${link.linked_by}` : ''}
        </div>
      </div>
      <div className="rl-wb-sync" data-status={link.last_sync_status || 'none'} data-testid="workbook-sync-status" role="status">
        {syncStatusText(link)}
      </div>
      <p className="rl-muted" style={{ fontSize: 12, margin: 0 }}>
        The workbook owns the catalog and pricing: TYPEs added in PROJECT BOM appear on the board. The board writes only quantities to AREA/ZONE BOM, plus a TYPE-only row in PROJECT BOM for a type you create here.
      </p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {p.canEdit ? (
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={link.auto_sync !== false} disabled={!!p.busy} onChange={(e) => p.onAutoSync(e.target.checked)} data-testid="workbook-auto-sync" />
            Auto-sync both ways
          </label>
        ) : null}
        {p.canEdit ? (
          <button className="rl-btn" data-size="sm" disabled={!!p.busy || syncing} onClick={p.onSyncNow} data-testid="workbook-sync-now">
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        ) : null}
        <a className="rl-btn" data-size="sm" href={link.url} target="_blank" rel="noopener noreferrer">
          Open workbook
        </a>
      </div>
      {conflicts.length ? (
        <div className="rl-wb-conflicts" data-testid="workbook-conflicts">
          <strong>Needs a look ({conflicts.length})</strong>
          <ul style={{ margin: '4px 0', paddingLeft: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
            {conflicts.map((c) => (
              <li key={c.id} className="rl-wb-conflict" data-kind={c.kind} data-testid={`workbook-conflict-${c.kind}`}>
                <div style={{ fontSize: 13 }}>{c.message}</div>
                {c.field ? (
                  <div className="rl-muted" style={{ fontSize: 12 }}>
                    {c.field}: board <code>{c.board}</code> · sheet <code>{c.sheet}</code>
                  </div>
                ) : null}
                {p.canEdit ? (
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    {(CONFLICT_ACTIONS[c.kind] ?? [{ action: 'dismiss' as const, label: 'OK' }]).map((a) => (
                      <button key={a.action} className="rl-btn" data-size="sm" disabled={!!p.busy} onClick={() => p.onResolve(c.id, a.action)} data-testid={`workbook-conflict-${a.action}`}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {link.left_to_workbook?.length ? (
        <div className="rl-wb-conflict" data-testid="workbook-left-to-workbook" style={{ fontSize: 13 }}>
          {link.left_to_workbook.map((x) => (
            <div key={x.type}>
              {x.type} was deleted on the board but is still in the workbook{x.row ? ` (PROJECT BOM row ${x.row})` : ''} — delete it there to remove it.
            </div>
          ))}
        </div>
      ) : null}
      {link.suppressed?.length ? (
        <details data-testid="workbook-suppressed">
          <summary className="rl-muted" style={{ fontSize: 12 }}>
            {link.suppressed.length} quantity row{link.suppressed.length === 1 ? '' : 's'} not written (left to the workbook)
          </summary>
          <ul style={{ margin: '4px 0', paddingLeft: 18, fontSize: 12 }}>
            {link.suppressed.map((x) => (
              <li key={x.key}>
                {x.type} · {x.area || '—'}{' '}
                {p.canEdit ? (
                  <button className="rl-btn" data-size="sm" data-variant="ghost" onClick={() => p.onRestore(x.key)}>
                    Write again
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {link.notices?.length ? (
        <details>
          <summary className="rl-muted" style={{ fontSize: 12 }}>Recent sync notes</summary>
          <ul style={{ margin: '4px 0', paddingLeft: 18, fontSize: 12 }}>
            {link.notices.map((n, i) => (
              <li key={i}>{n.message}</li>
            ))}
          </ul>
        </details>
      ) : null}
      <details data-testid="workbook-advanced">
        <summary className="rl-muted" style={{ fontSize: 12 }}>Advanced: preview, push, read back</summary>
        <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
      <div className="rl-muted" style={{ fontSize: 12 }} data-testid="workbook-last-push">
        {link.last_push_at ? (
          <>
            {STATUS_LABEL[link.last_push_status || ''] ?? 'Last push'} {relativeTime(link.last_push_at)}
            {link.last_push_by ? ` by ${link.last_push_by}` : ''}
            {last?.counts ? ` — ${Object.entries(last.counts).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(', ')}` : ''}
            {last?.error ? ` — ${last.error}` : ''}
          </>
        ) : (
          'Not pushed yet.'
        )}
      </div>
      {p.canEdit ? (
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={p.clean} onChange={(e) => p.setClean(e.target.checked)} data-testid="workbook-clean" />
          Clear leftover non-standard prefixes from REFERENCE TABLES (this copy only)
        </label>
      ) : null}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {p.canEdit ? (
          <>
            <button className="rl-btn" data-size="sm" disabled={!!p.busy || pushing} onClick={p.onPreview} data-testid="workbook-preview">
              {p.busy === 'plan' ? 'Reading the workbook…' : 'Preview push'}
            </button>
            <button className="rl-btn" data-size="sm" data-variant="primary" disabled={!!p.busy || pushing} onClick={p.onPush} data-testid="workbook-push">
              {pushing ? 'Pushing…' : 'Push to workbook'}
            </button>
          </>
        ) : null}
        <button className="rl-btn" data-size="sm" disabled={!!p.busy} onClick={p.onReadback} data-testid="workbook-readback">
          {p.busy === 'readback' ? 'Reading…' : 'Read back'}
        </button>
        {p.canEdit ? (
          <button className="rl-btn" data-size="sm" data-variant="ghost" disabled={!!p.busy || pushing} onClick={p.onUnlink} data-testid="workbook-unlink">
            Unlink
          </button>
        ) : null}
      </div>
      {plan && s ? (
        <div className="rl-workbook-plan" data-testid="workbook-plan">
          <strong>{s.nothingToDo ? 'Nothing to write — the workbook already matches.' : `Push will write ${s.writes} row${s.writes === 1 ? '' : 's'}`}</strong>
          <ul style={{ margin: '4px 0', paddingLeft: 18, fontSize: 13 }}>
            <li>
              PROJECT BOM: {s.projectBom.add} add, {s.projectBom.update} update{s.projectBom.remove ? `, ${s.projectBom.remove} clear` : ''}
            </li>
            <li>
              AREA/ZONE BOM: {s.areaZone.add} add, {s.areaZone.update} update{s.areaZone.remove ? `, ${s.areaZone.remove} clear` : ''}
            </li>
            {s.unchanged ? <li>{s.unchanged} unchanged</li> : null}
            {s.prefixes ? <li>{s.prefixes} leftover prefix row{s.prefixes === 1 ? '' : 's'} cleared in REFERENCE TABLES</li> : null}
            {plan.prefixes.unknown.length ? <li>Unknown TYPE prefixes (category won't resolve): {plan.prefixes.unknown.join(', ')}</li> : null}
          </ul>
          {s.skipped.length ? (
            <table className="rl-table" style={{ fontSize: 12, width: '100%' }} data-testid="workbook-skips">
              <thead>
                <tr>
                  <th align="left">Tab</th>
                  <th align="left">Row</th>
                  <th align="left">TYPE</th>
                  <th align="left">Left alone because</th>
                </tr>
              </thead>
              <tbody>
                {s.skipped.map((a) => (
                  <tr key={`${a.tab}:${a.key}`}>
                    <td>{a.tab}</td>
                    <td>{a.row ?? '—'}</td>
                    <td>{a.type}</td>
                    <td>{a.reason || ACTION_LABEL[a.action]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}
      {readback?.rows?.length ? (
        <div data-testid="workbook-readback-result">
          <strong>{readback.ok ? 'Read back: every quantity and category matches.' : `Read back: ${readback.issues} row${readback.issues === 1 ? '' : 's'} need a look`}</strong>
          <table className="rl-table" style={{ fontSize: 12, width: '100%' }}>
            <thead>
              <tr>
                <th align="left">TYPE</th>
                <th align="right">Workbook QTY</th>
                <th align="right">Takeoff</th>
                <th align="left">Category</th>
              </tr>
            </thead>
            <tbody>
              {readback.rows.map((r) => (
                <tr key={r.row} data-issue={r.issues.length ? 'yes' : undefined} style={r.issues.length ? { color: 'var(--rl-danger, #c62828)' } : undefined}>
                  <td>{r.type}</td>
                  <td align="right">{r.qty ?? '—'}</td>
                  <td align="right">{r.expected ?? '—'}</td>
                  <td>{r.issues.includes('category_unresolved') ? 'unresolved' : r.category}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
        </div>
      </details>
    </div>
  );
}

/** Budget tab when a workbook is linked: pricing lives there; show its own totals, read-only. */
export function WorkbookPricing({ link, compact = false }: { link: WorkbookLinkDTO; compact?: boolean }) {
  const t: WorkbookTotals = link.totals ?? {};
  return (
    <div className="rl-wb-pricing" data-testid="workbook-pricing">
      <p style={{ margin: '0 0 6px' }}>
        <strong>Pricing lives in the workbook.</strong>{' '}
        <a href={link.url} target="_blank" rel="noopener noreferrer" data-testid="workbook-pricing-open">
          Open {compact ? 'workbook' : link.title}
        </a>
      </p>
      {t.available && t.scopes?.length ? (
        <table className="rl-table" style={{ fontSize: 12, width: '100%' }} data-testid="workbook-totals">
          <thead>
            <tr>
              <th align="left">{t.tab || 'BID SUMMARY'}</th>
              <th align="right">Equipment</th>
              <th align="right">Total</th>
            </tr>
          </thead>
          <tbody>
            {t.scopes.map((x) => (
              <tr key={x.scope} data-option={isOptionScope(x.scope) || undefined} data-testid={`workbook-total-${x.scope}`}>
                <td>
                  {x.scope}
                  {isOptionScope(x.scope) ? <span className="rl-wb-option-tag">option</span> : null}
                </td>
                <td align="right">{x.equipment || '—'}</td>
                <td align="right">{x.total || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="rl-muted" style={{ fontSize: 12, margin: 0 }}>
          The workbook's totals will show here after the next sync.
        </p>
      )}
      {t.read_at ? <p className="rl-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>Read {relativeTime(t.read_at)} · read-only</p> : null}
    </div>
  );
}
